import { useTranslation } from 'react-i18next';

import React, { useMemo, useRef, useState } from 'react';

import {
  Code,
  Link as LinkIcon,
  ListBullets,
  ListNumbers,
  TextB,
  // TextH,
  TextItalic,
  TextUnderline,
} from '@thinkific/chalk/react-icons';
import { classNames } from '@thinkific/chalk/utils';

import type { Editor } from 'components/new/Posts/hooks/useEditor/useEditor';

import LinkForm from '../../hooks/useEditor/Extensions/Link/LinkForm';
import ActionButton from '../ActionButton';
import * as css from './MenuBar.css';

export interface MenuBarProps {
  className?: string;
  editor?: Editor;
  menuAccessories?: (editor: Editor) => React.ReactNode;
  onActionClick: (action: string) => void;
}

const MenuBar = ({ className, editor, menuAccessories, onActionClick }: MenuBarProps) => {
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const extensions = useMemo(() => {
    return editor?.options.extensions.map((extension) => extension.name);
  }, [editor]);
  const isReady = !!editor;
  const { t } = useTranslation();

  const linkRef = useRef<HTMLButtonElement>(null);

  const handleActionClick = (action: string, callback: () => void) => {
    onActionClick(action);
    callback();
  };

  const openLinkModal = () => {
    handleActionClick('link', () => editor?.chain().focus().run());
    setLinkModalOpen(true);
  };

  const closeLinkModal = () => {
    setLinkModalOpen(false);
    linkRef.current?.focus();
  };

  return (
    <div className={classNames(css.container, className)}>
      <div className={css.leftContent}>
        {/* {extensions?.includes('heading') && (
          <ActionButton
            arialLabel={t(
              'components-documentEditor-menuBar_headingSize1',
              'heading'
            )}
            disabled={!isReady}
            icon={TextH}
            isActive={isReady && editor.isActive('heading', { level: 2 })}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 2 }).run()
            }
          />
        )} */}
        {extensions?.includes('bold') && (
          <ActionButton
            arialLabel={t('components-documentEditor-menuBar_bold', 'bold')}
            dataQa="post-editor__bold"
            disabled={!isReady || !editor.can().chain().focus().toggleBold().run()}
            icon={TextB}
            isActive={isReady && editor.isActive('bold')}
            onClick={() =>
              handleActionClick('bold', () => editor?.chain().focus().toggleBold().run())
            }
          />
        )}
        {extensions?.includes('italic') && (
          <ActionButton
            arialLabel={t('components-documentEditor-menuBar_italic', 'italic')}
            dataQa="post-editor__italic"
            disabled={!isReady || !editor.can().chain().focus().toggleItalic().run()}
            icon={TextItalic}
            isActive={isReady && editor.isActive('italic')}
            onClick={() =>
              handleActionClick('italic', () => editor?.chain().focus().toggleItalic().run())
            }
          />
        )}
        {extensions?.includes('underline') && (
          <ActionButton
            arialLabel={t('components-documentEditor-menuBar_underline', 'underline')}
            dataQa="post-editor__underline"
            disabled={!isReady || !editor.can().chain().focus().toggleUnderline().run()}
            icon={TextUnderline}
            isActive={isReady && editor.isActive('underline')}
            onClick={() =>
              handleActionClick('underline', () => editor?.chain().focus().toggleUnderline().run())
            }
          />
        )}
        {extensions?.includes('bulletList') && (
          <ActionButton
            arialLabel={t('components-documentEditor-menuBar_bulletList', 'bullet list')}
            dataQa="post-editor__bullet-list"
            disabled={!isReady}
            icon={ListBullets}
            isActive={isReady && editor.isActive('bulletList')}
            onClick={() =>
              handleActionClick('bulletList', () =>
                editor?.chain().focus().toggleBulletList().run()
              )
            }
          />
        )}
        {extensions?.includes('orderedList') && (
          <ActionButton
            arialLabel={t('components-documentEditor-menuBar_orderedList', 'ordered list')}
            dataQa="post-editor__ordered-list"
            disabled={!isReady}
            icon={ListNumbers}
            isActive={isReady && editor.isActive('orderedList')}
            onClick={() =>
              handleActionClick('orderedList', () =>
                editor?.chain().focus().toggleOrderedList().run()
              )
            }
          />
        )}
        {extensions?.includes('code') && (
          <ActionButton
            arialLabel={t('components-documentEditor-menuBar_code', 'code')}
            dataQa="post-editor__code"
            disabled={!isReady}
            icon={Code}
            isActive={isReady && editor.isActive('code')}
            onClick={() =>
              handleActionClick('code', () => editor?.chain().focus().toggleCode().run())
            }
          />
        )}
        {extensions?.includes('link') && (
          <div className={css.linkButtonContainer}>
            <ActionButton
              arialLabel={t('components-documentEditor-menuBar_link', 'link')}
              dataQa="post-editor__link"
              disabled={!isReady}
              icon={LinkIcon}
              isActive={isReady && linkModalOpen}
              ref={linkRef}
              onClick={openLinkModal}
            />

            <LinkForm
              editor={editor}
              isOpen={linkModalOpen}
              onChange={closeLinkModal}
              onClose={closeLinkModal}
            />
          </div>
        )}
        {menuAccessories && menuAccessories(editor)}
      </div>
    </div>
  );
};

export default MenuBar;
